import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import StbMultiSelectCollapsible, { StbMultiSelectGroupItem } from './StbMultiSelectCollapsible';
import { HbrIcon, HbrInput } from '@harbor/elements-react-standalone';
import { magnifyingGlass, statusInfoSquare } from '@harbor/elements/icons';
import { asSingleValue } from '../../support/utils';

interface StbMultiSelectGroup {
  groupLabel: string;
  items: StbMultiSelectGroupItem[];
}

interface StbMultiSelectGroupedWithSearchProps {
  items: StbMultiSelectGroupItem[];
  selected: StbMultiSelectGroupItem[];
  groupBy: string;
  onChange: (selected: StbMultiSelectGroupItem[]) => void;
  className?: string;
  inline?: boolean;
  placeholder?: string;
  loading?: boolean;
  boundaryRef?: React.RefObject<HTMLElement>;
}

const StbMultiSelectGroupedWithSearch: React.FC<StbMultiSelectGroupedWithSearchProps> = ({
                                                                                           items,
                                                                                           selected,
                                                                                           groupBy,
                                                                                           onChange,
                                                                                           className,
                                                                                           inline = false,
                                                                                           placeholder = 'Search items',
                                                                                           loading = false,
                                                                                           boundaryRef,
                                                                                         }) => {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search);
  const debounceDelay = 300;

  const wrapper = useRef<HTMLDivElement | null>(null);
  const [openDropdown, setOpenDropdown] = useState<boolean>(true);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const boundaryEl = boundaryRef?.current ?? wrapper.current;
      if (!inline && boundaryEl && !boundaryEl.contains(e.target as Node)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [inline, boundaryRef]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, debounceDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [search, debounceDelay]);

  const generateGroups = useCallback(
    (items: StbMultiSelectGroupItem[], groupBy: string): { [key: string]: any[] } => {
      if (!items || items.length === 0) return {};
      if (!Object.keys(items[0]).includes(groupBy)) return { [groupBy]: items };

      return items.reduce((groups, item) => {
        const key = item[groupBy];
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
        return groups;
      }, {} as { [key: string]: any[] });
    },
    [],
  );

  const selectedGroups: Record<string, StbMultiSelectGroupItem[]> = useMemo(() => {
    return generateGroups(selected, groupBy);
  }, [selected, groupBy, generateGroups]);

  const currentGroups: StbMultiSelectGroup[] = useMemo(() => {
    if (!items || items.length === 0) return [];

    const sortedItems =
      debouncedSearch === ''
        ? [...items].sort((a, b) => a.name.localeCompare(b.name))
        : [...items]
          .filter((item) => item.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
          .sort((a, b) => a.name.localeCompare(b.name));

    const groupedItems = generateGroups(sortedItems, groupBy);

    return Object.entries(groupedItems)
      .map(([groupLabel, items]) => ({ groupLabel, items }))
      .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
  }, [items, groupBy, debouncedSearch, generateGroups]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <div
      ref={wrapper}
      className={`stb-multiselect relative flex flex-col min-w-0 w-full ${className ? className : ''}`}
    >
      <HbrInput
        placeholder={placeholder}
        value={search}
        clearable={true}
        onHbr-input={(e) => handleSearchChange(asSingleValue(e.target.value))}
        className="ml-0 mb-2 w-full min-w-0"
        onHbr-focus={() => setOpenDropdown(true)}
      >
        <HbrIcon
          slot="prefix"
          name="magnifying-glass"
          asset={magnifyingGlass}
          size="16px"
          staticColors={true}
        />
      </HbrInput>

      {openDropdown && (
        <div
          className={
            inline
              ? 'w-full min-w-0'
              : 'absolute left-0 mt-10 z-50 bg-white shadow-lg w-full min-w-0'
          }
        >
          {!loading && currentGroups.length === 0 ? (
            <div className="bg-blue-100 border border-blue-200 text-blue-800 w-full px-4 py-3 rounded-md mb-4 flex items-center gap-2">
              <HbrIcon asset={statusInfoSquare} sentiment="info" />
              No items found
            </div>
          ) : (
            <div className="border border-gray-400 divide-y divide-gray-300 w-full min-w-0 h-96 rounded-md overflow-y-auto overflow-x-hidden max-h-72">
              {currentGroups.map(({ groupLabel, items }) => (
                <StbMultiSelectCollapsible
                  key={groupLabel}
                  label={groupLabel}
                  items={items}
                  selectedItems={selectedGroups[groupLabel] || []}
                  onChange={(updatedItems: StbMultiSelectGroupItem[]) => {
                    const originalSelection = selectedGroups[groupLabel] || [];
                    const displayedItems = items;
                    const update: StbMultiSelectGroupItem[] = [];

                    for (const item of originalSelection) {
                      if (!displayedItems.find((i) => i.id === item.id)) {
                        update.push(item);
                      } else if (updatedItems.find((i) => i.id === item.id)) {
                        update.push(item);
                      }
                    }

                    for (const item of updatedItems) {
                      if (!originalSelection.find((i) => i.id === item.id)) {
                        update.push(item);
                      }
                    }

                    const updatedSelected = { ...selectedGroups, [groupLabel]: update };
                    onChange(Object.values(updatedSelected).flat());
                  }}
                  open={currentGroups.length === 1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StbMultiSelectGroupedWithSearch;